import React from 'react';
import type { BoardDrawing } from '../../types';
import { ENG_CALLOUT_FILL, wrapLabel } from '../pdf/engineeringMarkup';
import { CALLOUT_ARROW, CALLOUT_STROKE, FRAME_FILL } from './boardConstants';
import { calloutBoxPoint, calloutBoxSize, isFrameDrawing, sortDrawings } from './drawUtils';

const CALLOUT_SERIF = 'Georgia, "Iowan Old Style", Palatino, "Times New Roman", serif';

function leaderPath(points: BoardDrawing['points']): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
}

/** Arrow tip at `tip`, pointing from `from` toward the pin. */
function calloutArrowPath(
  tip: { x: number; y: number },
  from: { x: number; y: number },
  size: number,
): string {
  let dx = tip.x - from.x;
  let dy = tip.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) {
    dx = -1;
    dy = 0;
  } else {
    dx /= len;
    dy /= len;
  }
  const px = -dy;
  const py = dx;
  const backX = tip.x - dx * size;
  const backY = tip.y - dy * size;
  const half = size * 0.42;
  return `M ${tip.x} ${tip.y} L ${backX + px * half} ${backY + py * half} L ${backX - px * half} ${backY - py * half} Z`;
}

const ARROW_MARKER_ID = 'board-draw-arrow';

function polylinePoints(d: BoardDrawing): string {
  return d.points.map((p) => `${p.x},${p.y}`).join(' ');
}

function bounds(d: BoardDrawing): { x: number; y: number; w: number; h: number } {
  const a = d.points[0];
  const b = d.points[1] || a;
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
}

interface DrawingShapeProps {
  drawing: BoardDrawing;
  /** When true, coordinates are 0–1 (image overlay). */
  normalized?: boolean;
  selected?: boolean;
  selectable?: boolean;
  onSelect?: (drawing: BoardDrawing, e: React.PointerEvent) => void;
  onDoubleClick?: (drawing: BoardDrawing) => void;
}

export const DrawingShape: React.FC<DrawingShapeProps> = ({
  drawing,
  normalized,
  selected,
  selectable,
  onSelect,
  onDoubleClick,
}) => {
  const { kind, color, width, opacity } = drawing;
  const strokeW = Math.max(width, normalized ? 0.004 : 1);
  const interactive = selectable && (kind === 'rect' || kind === 'ellipse' || kind === 'line' || kind === 'arrow' || kind === 'callout');
  const common = {
    stroke: selected ? '#6366f1' : color,
    strokeWidth: selected ? strokeW + (normalized ? 0.006 : 1.5) : strokeW,
    fill: isFrameDrawing(drawing)
      ? normalized
        ? 'transparent'
        : drawing.fill || FRAME_FILL
      : kind === 'ellipse'
        ? 'transparent'
        : ('none' as const),
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    opacity: opacity ?? 1,
    style: {
      ...(kind === 'highlighter' ? { mixBlendMode: 'multiply' as const } : {}),
      pointerEvents: interactive ? ('all' as const) : ('none' as const),
      cursor: interactive ? 'move' : undefined,
    },
    onPointerDown: interactive
      ? (e: React.PointerEvent) => {
          e.stopPropagation();
          onSelect?.(drawing, e);
        }
      : undefined,
  };

  if (kind === 'pen' || kind === 'highlighter') {
    if (drawing.points.length < 2) {
      const p = drawing.points[0];
      if (!p) return null;
      return <circle cx={p.x} cy={p.y} r={strokeW / 2} fill={color} opacity={common.opacity} />;
    }
    return <polyline points={polylinePoints(drawing)} {...common} />;
  }

  if (kind === 'line' && drawing.points.length >= 2) {
    const [a, b] = drawing.points;
    return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} {...common} />;
  }

  if (kind === 'arrow' && drawing.points.length >= 2) {
    const [a, b] = drawing.points;
    return (
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        {...common}
        markerEnd={`url(#${ARROW_MARKER_ID})`}
      />
    );
  }

  if ((kind === 'rect' || kind === 'ellipse') && drawing.points.length >= 2) {
    const { x, y, w, h } = bounds(drawing);
    if (kind === 'rect') return <rect x={x} y={y} width={w} height={h} {...common} />;
    return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} {...common} />;
  }

  if (kind === 'callout' && drawing.points.length >= 2) {
    const pin = drawing.points[0];
    const box = calloutBoxPoint(drawing) || pin;
    const { w: boxW, h: boxH } = calloutBoxSize(drawing, !!normalized);
    const bx = box.x - boxW / 2;
    const by = box.y - boxH / 2;
    const initials = (drawing.authorInitials || '').trim().toUpperCase();
    const note = (drawing.text || '').trim();
    const noteLines = note ? wrapLabel(note) : [];
    const ink = selected ? '#6366f1' : color;
    const calloutStroke = normalized ? CALLOUT_STROKE / 280 : CALLOUT_STROKE;
    const arrowSize = normalized ? CALLOUT_ARROW / 280 : CALLOUT_ARROW;
    const toward = drawing.points[1] || box;
    const noteSize = normalized ? 0.042 : 13;
    const lineH = normalized ? 0.052 : 16;
    const chipW = normalized ? 0.07 : 22;
    const chipH = normalized ? 0.03 : 13;
    const chipX = bx + boxW - chipW + (normalized ? 0.012 : 5);
    const chipY = by - chipH * 0.45;
    const chipFont = normalized ? 0.022 : 8;
    return (
      <g
        className="board-draw-callout"
        style={{ pointerEvents: interactive ? 'auto' : 'none', cursor: interactive ? 'text' : undefined }}
        onPointerDown={common.onPointerDown}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClick?.(drawing);
        }}
      >
        <path
          d={leaderPath(drawing.points)}
          stroke={ink}
          strokeWidth={calloutStroke}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={common.opacity}
          style={common.style}
        />
        <path d={calloutArrowPath(pin, toward, arrowSize)} fill={ink} stroke="none" style={common.style} />
        <rect
          x={bx}
          y={by}
          width={boxW}
          height={boxH}
          fill={drawing.fill || ENG_CALLOUT_FILL}
          stroke={ink}
          strokeWidth={calloutStroke}
          style={common.style}
        />
        {noteLines.map((line, i) => (
          <text
            key={`${i}-${line}`}
            x={box.x}
            y={box.y + (i - (noteLines.length - 1) / 2) * lineH}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={ink}
            fontSize={noteSize}
            fontWeight={600}
            fontFamily={CALLOUT_SERIF}
            style={{ pointerEvents: 'none' }}
          >
            {line}
          </text>
        ))}
        {!note ? (
          <text
            x={box.x}
            y={box.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={ink}
            fontSize={noteSize}
            fontWeight={600}
            fontFamily={CALLOUT_SERIF}
            opacity={0.55}
            style={{ pointerEvents: 'none' }}
          >
            Note
          </text>
        ) : null}
        {initials ? (
          <g style={{ pointerEvents: 'none' }}>
            <rect
              x={chipX}
              y={chipY}
              width={chipW}
              height={chipH}
              rx={normalized ? 0.008 : 3}
              fill="#f8fafc"
              stroke="#cbd5e1"
              strokeWidth={normalized ? 0.003 : 0.75}
            />
            <text
              x={chipX + chipW / 2}
              y={chipY + chipH / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#64748b"
              fontSize={chipFont}
              fontWeight={600}
              letterSpacing={normalized ? 0.003 : 0.4}
              fontFamily="ui-sans-serif, system-ui, sans-serif"
            >
              {initials}
            </text>
          </g>
        ) : null}
      </g>
    );
  }

  return null;
};

interface DrawingsSvgProps {
  drawings: BoardDrawing[];
  preview?: BoardDrawing | null;
  normalized?: boolean;
  className?: string;
  style?: React.CSSProperties;
  markerId?: string;
  innerTransform?: string;
  selectedId?: string | null;
  selectable?: boolean;
  onSelect?: (drawing: BoardDrawing, e: React.PointerEvent) => void;
  onCalloutDoubleClick?: (drawing: BoardDrawing) => void;
  children?: React.ReactNode;
}

export const DrawingsSvg: React.FC<DrawingsSvgProps> = ({
  drawings,
  preview,
  normalized,
  className,
  style,
  markerId = ARROW_MARKER_ID,
  innerTransform,
  selectedId,
  selectable,
  onSelect,
  onCalloutDoubleClick,
  children,
}) => {
  const stacked = sortDrawings(drawings);
  return (
  <svg
    className={className}
    style={style}
    viewBox={normalized ? '0 0 1 1' : undefined}
    preserveAspectRatio={normalized ? 'none' : undefined}
    overflow="visible"
  >
    <defs>
      <marker
        id={markerId}
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth={normalized ? 0.08 : 7}
        markerHeight={normalized ? 0.08 : 7}
        orient="auto-start-reverse"
        markerUnits={normalized ? 'userSpaceOnUse' : 'strokeWidth'}
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
      </marker>
    </defs>
    <g transform={innerTransform}>
    {stacked.map((d) =>
      d.kind === 'arrow' && d.points.length >= 2 ? (
        <line
          key={d.id}
          x1={d.points[0].x}
          y1={d.points[0].y}
          x2={d.points[1].x}
          y2={d.points[1].y}
          stroke={selectedId === d.id ? '#6366f1' : d.color}
          strokeWidth={Math.max(d.width, normalized ? 0.004 : 1)}
          strokeLinecap="round"
          fill="none"
          opacity={d.opacity ?? 1}
          markerEnd={`url(#${markerId})`}
          style={{
            pointerEvents: selectable ? 'stroke' : 'none',
            cursor: selectable ? 'move' : undefined,
          }}
          onPointerDown={
            selectable
              ? (e) => {
                  e.stopPropagation();
                  onSelect?.(d, e);
                }
              : undefined
          }
        />
      ) : (
        <DrawingShape
          key={d.id}
          drawing={d}
          normalized={normalized}
          selected={selectedId === d.id}
          selectable={selectable}
          onSelect={onSelect}
          onDoubleClick={onCalloutDoubleClick}
        />
      ),
    )}
    {preview &&
      (preview.kind === 'arrow' && preview.points.length >= 2 ? (
        <line
          x1={preview.points[0].x}
          y1={preview.points[0].y}
          x2={preview.points[1].x}
          y2={preview.points[1].y}
          stroke={preview.color}
          strokeWidth={Math.max(preview.width, normalized ? 0.004 : 1)}
          strokeLinecap="round"
          fill="none"
          markerEnd={`url(#${markerId})`}
        />
      ) : (
        <DrawingShape drawing={preview} normalized={normalized} />
      ))}
    {children}
    </g>
  </svg>
  );
};
